/** Exercises and blocks as the app shows them (PlannedExercise.record, TargetText). */
import { exerciseKey } from "./text.js";
import type { Block, Exercise, RecordKind, Session, Target, Workout } from "./types.js";

// The key the phone gave a planned exercise, from a log of it when there is one. It is learned
// from every session the portal reads, as the old page did, so a renamed lift still matches.
const knownKeys = new Map<string, string>();

export function learnKeys(session: Session | null | undefined): void {
  for (const log of session?.logs ?? []) {
    if (log.exerciseID && log.exerciseKey) knownKeys.set(String(log.exerciseID).toUpperCase(), log.exerciseKey);
  }
}

export function keyOf(exercise: Exercise): string {
  return knownKeys.get(String(exercise.id ?? "").toUpperCase()) ?? exerciseKey(exercise.name ?? "");
}

export function norm(id: unknown): string {
  return id == null ? "" : String(id).toUpperCase();
}

/** Every log skipped, and at least one (§61.8): the athlete showed up to say they didn't do it. */
export function allSkipped(logs: readonly { skipped?: boolean | null }[]): boolean {
  return logs.length > 0 && logs.every((l) => !!l.skipped);
}

export function skippedCount(logs: readonly { skipped?: boolean | null }[]): number {
  return logs.filter((l) => !!l.skipped).length;
}

/** A workout's own blocks: not the day's other parts riding on it (§61.4). */
export function ownBlocks(w: Pick<Workout, "session">): Block[] {
  return (w.session.blocks ?? []).filter((b) => !b.loggedFrom);
}

/** A workout every set of whose own blocks was skipped. */
export function skippedWorkout(w: Pick<Workout, "session">): boolean {
  const ids = new Set(ownBlocks(w).flatMap((b) => (b.exercises ?? []).map((e) => norm(e.id))));
  return allSkipped((w.session.logs ?? []).filter((l) => ids.has(norm(l.exerciseID))));
}

export function working(blocks: Block[] | null | undefined): Block[] {
  return (blocks ?? []).filter((b) => !b.isPrep);
}
/** The lifts: working blocks, without the day's other sessions an athlete logged into the workout (§9.2). */
export function liftBlocks(blocks: Block[] | null | undefined): Block[] {
  return working(blocks).filter((b) => !b.loggedFrom);
}

/** A workout that is only the day's other sessions logged, no lifts of its own. */
export function isLoggedOnly(blocks: Block[] | null | undefined): boolean {
  const list = working(blocks);
  return list.length > 0 && list.every((b) => b.loggedFrom);
}

export function prepBlocks(blocks: Block[] | null | undefined): Block[] {
  return (blocks ?? []).filter((b) => b.isPrep);
}

export function recordOf(exercise: Exercise): RecordKind {
  if (exercise.recordOverride) return exercise.recordOverride;
  const unit = exercise.target?.unit;
  if (unit === "meters") return "time";
  if (exercise.isBodyweight || unit === "seconds") return "reps";
  return "weightReps";
}

export const RECORD_LABEL: { [K in RecordKind]: string } = { weightReps: "Weight × reps", reps: "Reps", time: "Time", done: "Done" };

function groupCount(exercise: Exercise): number | null {
  return exercise.groups != null && exercise.groups > 1 ? exercise.groups : null;
}
function targetNumber(target: Target): string {
  if (target.upper != null && target.upper > target.amount) return target.amount + "–" + target.upper;
  return String(target.amount);
}
export function unitSuffix(unit: string | null | undefined): string {
  return unit === "seconds" ? " s" : unit === "meters" ? " m" : "";
}

export function plural(n: number, one: string, many: string): string {
  return n === 1 ? "1 " + one : n + " " + many;
}
const roundsText = (n: number) => plural(n, "round", "rounds");
export const setsText = (n: number) => plural(n, "set", "sets");

export function targetText(exercise: Exercise): string | null {
  const t = exercise.target;
  if (!t) return exercise.perSide ? "Each side" : null;
  const groups = groupCount(exercise);
  const number = (groups ? groups + " × " : "") + targetNumber(t);
  if (t.unit === "seconds") return number + " s" + (exercise.perSide ? " each side" : "");
  if (t.unit === "meters") return number + " m" + (exercise.perSide ? " each side" : "");
  if (exercise.perSide) return number + " each side";
  return t.upper == null && t.amount === 1 && !groups ? "1 rep" : number + " reps";
}

function compactTarget(exercise: Exercise): string | null {
  const t = exercise.target;
  if (!t) return null;
  const groups = groupCount(exercise);
  return (groups ? groups + "×" : "") + targetNumber(t) + unitSuffix(t.unit) + (exercise.perSide ? " each side" : "");
}

function sameTarget(a: Target | null | undefined, b: Target | null | undefined): boolean {
  if (!a || !b) return a == b;
  return a.amount === b.amount && (a.upper ?? null) === (b.upper ?? null) && a.unit === b.unit;
}

function isRepeats(block: Block): boolean {
  const list = block.exercises ?? [];
  const first = list[0];
  if (list.length < 2 || !first) return false;
  const key = keyOf(first);
  return list.every((e) => keyOf(e) === key && (e.note || null) === (first.note || null) && sameTarget(e.target, first.target));
}

function blockKind(block: Block): "straight" | "superset" | "circuit" {
  const n = (block.exercises ?? []).length;
  return n <= 1 ? "straight" : n === 2 ? "superset" : "circuit";
}

export function blockTarget(block: Block): string | null {
  const list = block.exercises ?? [];
  const first = list[0];
  if (isRepeats(block) && first) {
    const c = compactTarget(first);
    return roundsText(block.sets) + " of " + (c ? list.length + " × " + c : String(list.length));
  }
  if (blockKind(block) !== "straight" || !first) return null;
  const each = compactTarget(first);
  if (groupCount(first) && each) return roundsText(block.sets) + " of " + each;
  return each ? block.sets + " × " + each : setsText(block.sets);
}

export function blockLabel(block: Block): string {
  if (block.isPrep) return block.sets > 1 ? "Warm-up · " + roundsText(block.sets) : "Warm-up";
  if (isRepeats(block)) return roundsText(block.sets);
  const kind = blockKind(block);
  if (kind === "superset") return "Superset · " + roundsText(block.sets);
  if (kind === "circuit") return "Circuit · " + roundsText(block.sets);
  return setsText(block.sets);
}

/** The lift's target as the coach app heads a session with it. */
export function liftTarget(exercise: Exercise, block: Block): string | null {
  return blockKind(block) === "straight" ? blockTarget(block) : targetText(exercise);
}

export function liftKeys(blocks: Block[] | null | undefined): string[] {
  const keys: string[] = [];
  for (const b of liftBlocks(blocks)) {
    for (const e of b.exercises ?? []) {
      const k = keyOf(e);
      if (!keys.includes(k)) keys.push(k);
    }
  }
  return keys;
}

export function planTitle(plan: { title?: string | null } | null | undefined): string {
  return (plan?.title && String(plan.title).trim()) || "Weights";
}

export function byName(a: { name: string }, b: { name: string }): number {
  return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
}
